import { useEffect, useRef, useState } from 'react';
import type { Controls, StepMerged, StepTarget } from 'react-joyride';

/**
 * What a step asks the user to do before moving on (step `data.action`):
 * - `click`: click the target; the tour moves on by itself;
 * - `fill`: give the target field a value; « Suivant » shows up once it has one.
 */
export interface StepAction {
  kind: 'click' | 'fill';
  /** Shown instead of « Suivant », e.g. « Clique sur « Lancer l'env » ». */
  hint: string;
}

export interface StepData {
  primaryLabel?: string;
  action?: StepAction;
}

export function resolveTarget(target: StepTarget): HTMLElement | null {
  if (typeof target === 'string') {
    return target === 'body'
      ? null
      : document.querySelector<HTMLElement>(target);
  }
  if (typeof target === 'function') return target();
  if (target && 'current' in target) return target.current;
  return target ?? null;
}

/** Value of a field: its own, or the one of the input inside it (MUI TextField, Autocomplete). */
export function fieldValue(element: HTMLElement | null): string {
  if (!element) return '';
  const input =
    element instanceof HTMLInputElement ||
    element instanceof HTMLTextAreaElement
      ? element
      : element.querySelector<HTMLInputElement | HTMLTextAreaElement>(
          'input:not([type="hidden"]), textarea',
        );
  return input?.value.trim() ?? '';
}

/**
 * Watches the user doing a step's action. Returns the element to point at (for the hotspot)
 * and whether the action is done.
 */
export function useStepAction(
  step: StepMerged,
  index: number,
  controls: Controls,
) {
  const action = (step.data as StepData | undefined)?.action;
  const kind = action?.kind;
  // Joyride hands a new step object on every render: read the latest one, react to `index`.
  const stepRef = useRef(step);
  stepRef.current = step;
  const controlsRef = useRef(controls);
  controlsRef.current = controls;
  const [element, setElement] = useState<HTMLElement | null>(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    setDone(false);
    if (!kind) return undefined;
    // Targets re-render (React keys, drawers animating): resolve them again each time.
    const find = () => resolveTarget(stepRef.current.target);
    setElement(find());

    if (kind === 'click') {
      let timer: number | undefined;
      const onClick = (event: MouseEvent) => {
        const target = find();
        if (!target?.contains(event.target as Node)) return;
        setDone(true);
        // Let the click do its work (open a drawer, switch a filter) before the next step.
        timer = window.setTimeout(() => controlsRef.current.next(), 300);
      };
      document.addEventListener('click', onClick, true);
      return () => {
        document.removeEventListener('click', onClick, true);
        window.clearTimeout(timer);
      };
    }

    const poll = window.setInterval(() => {
      const target = find();
      setElement(prev => (prev === target ? prev : target));
      setDone(Boolean(fieldValue(target)));
    }, 300);
    return () => window.clearInterval(poll);
  }, [index, kind]);

  return { action, element, done };
}
